// dealing.js — Pure dealing utility functions for T-bill auction bidding
// Separate from fixedIncome.js because T-bills use zero-coupon discount pricing (ACT/364)

// ─── T-bill Discount Pricing ───────────────────────────────────────────
// Kenya T-bills: price = face × (1 - yield × days/364)
export function calcDiscountPrice(faceValue, annualYield, tenorDays) {
  if (!faceValue || faceValue <= 0) return 0;
  if (!annualYield || annualYield <= 0) return faceValue;
  if (!tenorDays || tenorDays <= 0) return faceValue;
  const yieldDecimal = annualYield > 1 ? annualYield / 100 : annualYield;
  return faceValue * (1 - yieldDecimal * tenorDays / 364);
}

// ─── Net Yield (after WHT and fees) ────────────────────────────────────
export function calcNetYield(grossYield, whtPct = 15, feesBps = 0) {
  if (!grossYield) return 0;
  const afterWht = grossYield * (1 - whtPct / 100);
  const afterFees = afterWht - feesBps / 100;
  return Math.max(afterFees, 0);
}

// ─── Hedged Return (base-currency) ─────────────────────────────────────
export function calcHedgedReturn(localYield, spotRate, forwardRate, tenorDays) {
  if (!localYield || !spotRate || !forwardRate || !tenorDays) return 0;
  const yieldDecimal = localYield > 1 ? localYield / 100 : localYield;
  const fxCost = (forwardRate - spotRate) / spotRate;
  const annualizedFxCost = fxCost * (364 / tenorDays);
  return (yieldDecimal + annualizedFxCost) * 100;
}

// ─── Unhedged Return Estimate ──────────────────────────────────────────
export function calcUnhedgedReturn(localYield, spotRate, expectedSpot) {
  if (!localYield || !spotRate || !expectedSpot) return 0;
  const yieldDecimal = localYield > 1 ? localYield / 100 : localYield;
  const fxChange = (expectedSpot - spotRate) / spotRate;
  return (yieldDecimal + fxChange) * 100;
}

// ─── Round to Bid Multiple ─────────────────────────────────────────────
export function roundToBidMultiple(amount, denomination = 50000) {
  if (!amount || amount <= 0) return { rounded: 0, delta: 0 };
  const rounded = Math.round(amount / denomination) * denomination;
  return { rounded, delta: rounded - amount };
}

// ─── Convert Base Currency to Local Bid Amount ─────────────────────────
export function convertBaseToBidLocal(baseAmount, baseCcy, spotRate, denomination = 50000) {
  if (!baseAmount || !spotRate) return { localAmount: 0, rounded: 0, delta: 0, baseCcy };
  const localAmount = baseAmount * spotRate;
  const { rounded, delta } = roundToBidMultiple(localAmount, denomination);
  return { localAmount, rounded, delta, baseCcy };
}

// ─── Validate a Single Bid Tranche ─────────────────────────────────────
export function validateBidTranche(tranche, rules, existingBids = []) {
  const errors = [];
  if (!tranche || !rules) return { valid: false, errors: ['Missing tranche or rules data'] };

  const { amount, type, yield: bidYield } = tranche;

  // Amount checks
  if (!amount || amount <= 0) {
    errors.push('Bid amount must be greater than zero');
  }

  if (amount % rules.denomination !== 0) {
    errors.push(`Amount must be in multiples of KES ${rules.denomination.toLocaleString()}`);
  }

  if (type === 'non_competitive') {
    if (amount < rules.minNonCompetitive) {
      errors.push(`Non-competitive minimum is KES ${rules.minNonCompetitive.toLocaleString()}`);
    }
    if (amount > rules.maxNonCompetitive) {
      errors.push(`Non-competitive maximum is KES ${rules.maxNonCompetitive.toLocaleString()}`);
    }
  }

  if (type === 'competitive') {
    if (amount < rules.minCompetitive) {
      errors.push(`Competitive minimum is KES ${rules.minCompetitive.toLocaleString()}`);
    }
    if (bidYield === undefined || bidYield === null || bidYield <= 0) {
      errors.push('Competitive bids require a yield');
    }
  }

  // Aggregate check
  const totalExisting = existingBids.reduce((sum, b) => sum + (b.amount || 0), 0);
  if (totalExisting + amount > rules.maxNonCompetitive && type === 'non_competitive') {
    errors.push(`Total non-competitive bids would exceed KES ${rules.maxNonCompetitive.toLocaleString()}`);
  }

  return { valid: errors.length === 0, errors };
}

// ─── Full Ticket Validation ────────────────────────────────────────────
export function validateTicket(ticket, auction) {
  const errors = [];
  if (!ticket || !auction) return { valid: false, errors: ['Missing ticket or auction data'] };

  // Deadline check
  const now = new Date();
  const deadline = new Date(auction.bidDeadline);
  if (now > deadline) {
    errors.push('Auction bid deadline has passed');
  }

  // Tranches check
  if (!ticket.tranches || ticket.tranches.length === 0) {
    errors.push('At least one bid tranche is required');
  }

  // Total amount
  const totalAmount = (ticket.tranches || []).reduce((s, t) => s + (t.amount || 0), 0);
  if (totalAmount <= 0) {
    errors.push('Total bid amount must be greater than zero');
  }

  // Funding check
  if (ticket.fundingRoute === 'mobile_money' && totalAmount > 250000) {
    errors.push('Mobile money cap is KES 250,000 per transaction');
  }

  return { valid: errors.length === 0, errors };
}

// ─── Ticket State Machine ──────────────────────────────────────────────
export const TICKET_TRANSITIONS = {
  draft:               ['pending_approval', 'cancelled'],
  pending_approval:    ['approved', 'rejected', 'cancelled'],
  approved:            ['submitted_to_csd', 'cancelled'],
  rejected:            ['draft', 'cancelled'],
  submitted_to_csd:    ['allocated', 'partially_allocated', 'unsuccessful'],
  allocated:           ['payment_due'],
  partially_allocated: ['payment_due'],
  unsuccessful:        [],
  payment_due:         ['paid'],
  paid:                ['settled'],
  settled:             [],
  cancelled:           [],
};

export function canTransition(from, to) {
  return (TICKET_TRANSITIONS[from] || []).includes(to);
}

// ─── Netting Calculation ───────────────────────────────────────────────
export function calcNetting(paymentDue, sameDayMaturities = []) {
  const totalMaturities = sameDayMaturities.reduce((sum, m) => sum + (m.amount || 0), 0);
  const netPayable = Math.max(paymentDue - totalMaturities, 0);
  const nettingApplied = totalMaturities > 0;
  return { paymentDue, totalMaturities, netPayable, nettingApplied };
}

// ─── Currency Formatting ───────────────────────────────────────────────
const CURRENCY_SYMBOLS = {
  KES: 'KES', NGN: '₦', ZAR: 'R', GHS: 'GH₵', UGX: 'UGX',
  TZS: 'TZS', USD: '$', EUR: '€', GBP: '£',
};

export function formatLocalCurrency(value, currency = 'KES') {
  if (value === null || value === undefined) return '—';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const absVal = Math.abs(value);
  let formatted;
  if (absVal >= 1e9) formatted = `${(absVal / 1e9).toFixed(2)}B`;
  else if (absVal >= 1e6) formatted = `${(absVal / 1e6).toFixed(2)}M`;
  else if (absVal >= 1e3) formatted = absVal.toLocaleString('en-US', { maximumFractionDigits: 0 });
  else formatted = absVal.toFixed(2);
  return `${value < 0 ? '-' : ''}${symbol} ${formatted}`;
}

// ─── Auction Countdown ─────────────────────────────────────────────────
export function auctionCountdown(bidDeadline) {
  const now = new Date();
  const deadline = new Date(bidDeadline);
  const diff = deadline - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true, urgentClass: 'text-red-600' };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  let urgentClass = 'text-zinc-600';
  if (days === 0 && hours < 4) urgentClass = 'text-red-600 font-bold';
  else if (days === 0) urgentClass = 'text-orange-600';
  else if (days <= 1) urgentClass = 'text-amber-600';

  return { days, hours, minutes, seconds, expired: false, urgentClass };
}

// ─── Partial Fill Hedge Resizing ───────────────────────────────────────
export function calcPartialFillHedge(originalHedgePlan, allocatedFillPct) {
  if (!originalHedgePlan || !allocatedFillPct) return originalHedgePlan;
  const fillRatio = allocatedFillPct > 1 ? allocatedFillPct / 100 : allocatedFillPct;
  return {
    ...originalHedgePlan,
    notionalLocal: Math.round(originalHedgePlan.notionalLocal * fillRatio),
    notionalBase: originalHedgePlan.notionalBase * fillRatio,
    resized: true,
    originalNotionalLocal: originalHedgePlan.notionalLocal,
    fillPct: fillRatio * 100,
  };
}
